import { Pagination, Rsp } from "@repo/types";
import { getHttpClient } from "./http";
import type {
  AsyncDecodeResponse,
  DecodeTemplateListQuery,
  DecodeTemplateRecord,
  SendReceiveHistoryType,
  SendReceiveRecord,
  SendRecord,
  Template,
  TraceDecodeResult,
  UpdateForm,
  UploadForm,
  UseStatisticsItem,
} from "./template-trace.type";

// ── API ─────────────────────────────────────────────────────────

export const TemplatePtAPI = {
  getTemplateList(page: number, pageSize: number, searchValue: string = "") {
    return getHttpClient().get<Rsp<Pagination<Template[]>>>({
      url: `/psd/templates?page=${page}&page_size=${pageSize}` + (searchValue ? `&title=${searchValue}` : ""),
    });
  },

  getSendTemplates(page: number, pageSize: number) {
    return getHttpClient().get<Rsp<Pagination<Template[]>>>({
      url: `/psd/send-templates?page=${page}&page_size=${pageSize}`,
    });
  },

  getTemplateDetail(id: string, sendID?: string) {
    return getHttpClient().get<Rsp<Template>>({ url: `/psd/template/${id}` + (sendID ? `?send_id=${sendID}` : "") });
  },

  uploadTemplate(data: UploadForm[]) {
    return getHttpClient().post<Rsp<Template>>({ url: "/psd/upload-template?async=true", data });
  },

  sendTemplate(templateIds: string[], receiverUid: string[]) {
    return getHttpClient().post<Rsp<Template>>({
      url: "/psd/send-template",
      data: { template_id: templateIds.map((id) => Number(id)), receiver_uid: receiverUid },
    });
  },

  getReceivedTemplates(
    page: number,
    pageSize: number,
    received: boolean = true,
    searchValue: string = "",
  ) {
    return getHttpClient().get<Rsp<Pagination<Template[]>>>({
      url:
        `/psd/receive-templates?page=${page}&page_size=${pageSize}` +
        (received ? "&is_received=1" : "&is_received=0") +
        (searchValue ? `&title=${searchValue}` : ""),
    });
  },

  getSendRecord(templateId: string, page: number, pageSize: number) {
    return getHttpClient().get<Rsp<Pagination<SendRecord[]>>>({
      url: `/psd/send-templates?t_id=${templateId}&page=${page}&page_size=${pageSize}`,
    });
  },

  getDownloadToken(sendId: string, packType: "psd" | "png") {
    return getHttpClient().get<Rsp<{ name: string; path: string }[]>>({
      url: `/psd/download-send-template/${sendId}/${packType}`,
    });
  },

  /** 上传人下载自己上传的源文件，返回已签名的 PSD 地址 */
  downloadSourceTemplate(templateId: string) {
    return getHttpClient().get<Rsp<{ name: string; path: string }[]>>({
      url: `/psd/download-source-template/${templateId}`,
    });
  },

  updateTemplate(templateId: string, data: UpdateForm) {
    return getHttpClient().put<Rsp<Template>>({ url: `/psd/template/${templateId}`, data });
  },

  receiveTemplate(sendId: string) {
    return getHttpClient().post<Rsp<Template>>({ url: "/psd/receive-template", data: { send_id: sendId } });
  },

  retract(sendId: number) {
    return getHttpClient().post<Rsp<Template>>({
      url: "/psd/retract-send-template",
      data: { send_template_id: sendId },
    });
  },

  decodePsdTemplate(filePath: string, fileName?: string) {
    return getHttpClient().post<Rsp<AsyncDecodeResponse>>({
      url: "/psd/decode-template",
      data: {
        file_path: filePath,
        async: true,
        ...(fileName ? { file_name: fileName } : {}),
      },
    });
  },

  getDecodeTemplateList(query: DecodeTemplateListQuery = {}) {
    const params = new URLSearchParams();
    params.set("page", String(query.page ?? 1));
    params.set("page_size", String(query.page_size ?? 10));
    if (query.file_name) params.set("file_name", query.file_name);
    if (query.decode_id) params.set("decode_id", query.decode_id);
    if (query.status !== undefined) params.set("status", String(query.status));
    return getHttpClient().get<Rsp<Pagination<DecodeTemplateRecord[]>>>({
      url: `/psd/decode-template?${params.toString()}`,
    });
  },

  queryDecodeResult(decode_id: string) {
    return getHttpClient().get<
      Rsp<{
        wait?: boolean;
        data?: TraceDecodeResult[];
        error?: string;
      }>
    >({ url: `/psd/decode-template-result?decode_id=${decode_id}` });
  },

  getQuotaRemaining() {
    return getHttpClient().get<Rsp<{ send_num: number; decode_num: number }>>({
      url: "/psd/quota/remaining",
    });
  },

  retryMask(sendId: string) {
    return getHttpClient().post<Rsp<Template>>({
      url: "/psd/restart-send-template",
      data: { send_id: sendId },
    });
  },

  deleteTemplate(templateId: string) {
    return getHttpClient().delete<Rsp<void>>({ url: `/psd/template/${templateId}` });
  },

  /** 将已签收的文件转为永久存储（云空间） */
  storageReceiveTemplate(sendId: string) {
    return getHttpClient().post<Rsp<void>>({
      url: "/psd/storage/receive-templates",
      data: { send_id: sendId },
    });
  },

  /** 从云空间删除已永久存储的收货模板 */
  deleteStorageReceiveTemplate(sendId: string) {
    return getHttpClient().delete<Rsp<void>>({
      url: `/psd/storage/receive-templates?send_id=${encodeURIComponent(sendId)}`,
    });
  },

  clearExpiredReceivedTemplates() {
    return getHttpClient().delete<Rsp<void>>({
      url: "/psd/receive-templates/clear-expired",
    });
  },

  getUseStatistics(uids: string[]) {
    return getHttpClient().post<Rsp<UseStatisticsItem[]>>({ url: "/psd/use-statistics", data: { uids } });
  },

  getSendReceiveTemplates(
    page: number,
    pageSize: number,
    side: SendReceiveHistoryType,
    sender_id?: string,
    name?: string,
    receiver_id?: string,
    template_id?: string | number,
  ) {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
      side,
    });
    if (sender_id) params.set("uid", sender_id);
    if (name) params.set("title", name);
    if (receiver_id) params.set("receiver_uid", receiver_id);
    if (template_id) params.set("template_id", String(template_id));
    return getHttpClient().get<Rsp<Pagination<SendReceiveRecord[]>>>({
      url: `/psd/send-receive-templates?${params.toString()}`,
    });
  },
};
